// Phase I (I-B, I2): subscriptions, plans and entitlements. The Entitlements type is the I-B
// contract (docs/guides/roadmap/09-phase-i-plan.md, "Contracts"): getEntitlements() in the API
// returns it, the web app reads it to hide modules that are off.
import { z } from 'zod';

/** Modules a plan can switch on or off. Everything else is in every plan. */
export const PLAN_MODULES = {
  hr: 'HR',
  payroll: 'Payroll',
  monitoring: 'Monitoring',
  accounting: 'Accounting',
} as const;
export type PlanModule = keyof typeof PLAN_MODULES;
export const PLAN_MODULE_KEYS = Object.keys(PLAN_MODULES) as PlanModule[];

/** Feature flags a plan can carry (not routers). */
export const PLAN_FEATURES = {
  remove_powered_by: 'Remove "Powered by Denwen MSP"',
} as const;
export type PlanFeature = keyof typeof PLAN_FEATURES;

/** Numeric limits a plan may set. A limit that is absent means unlimited. */
export const PLAN_LIMITS = {
  seats: 'Technician seats',
  organizations: 'Client organizations',
  monitoredDevices: 'Monitored devices',
} as const;
export type PlanLimit = keyof typeof PLAN_LIMITS;

/** Stripe's subscription statuses, plus `none` for a tenant that has never had one. */
export const SUBSCRIPTION_STATUSES = ['none', 'trialing', 'active', 'past_due', 'unpaid', 'canceled', 'incomplete', 'incomplete_expired', 'paused'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];


/** What a tenant may use. The API enforces it; the web hides what is off. */
export interface Entitlements {
  tenantId: string;
  plan: {
    key: string;
    name: string;
    /** The "internal" plan: tenants from before Phase I, with everything on and no seat limit. */
    grandfathered: boolean;
  };
  status: SubscriptionStatus;
  seats: {
    /** Active staff users. Portal users are free and not counted. */
    used: number;
    /** null = unlimited. */
    limit: number | null;
  };
  modules: PlanModule[];
  features: PlanFeature[];
  limits: Partial<Record<PlanLimit, number>>;
  trialEndsAt: string | null;
}

// --- Plans (platform settings) ---------------------------------------------------------------

const priceId = z
  .string()
  .trim()
  .regex(/^price_[A-Za-z0-9]{6,}$/, 'A Stripe price id starts with price_')
  .max(100);
const optionalPriceId = z.preprocess((v) => (v === '' ? null : v), priceId.nullable().optional());

export const planAddonInput = z.object({
  key: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Lower case letters, digits and _'),
  name: z.string().trim().min(1, 'Required').max(80),
  stripePriceId: optionalPriceId,
  modules: z.array(z.enum(PLAN_MODULE_KEYS as [PlanModule, ...PlanModule[]])).max(10),
  /** flat = one per subscription; employee = one per active HR employee. */
  unit: z.enum(['flat', 'employee']).default('flat'),
  /** Shown in the app only. Stripe holds the real price. */
  priceMinor: z.number().int().min(0).max(10_000_000).nullable().optional(),
});
export type PlanAddon = z.infer<typeof planAddonInput>;

const limitsInput = z
  .object(Object.fromEntries(Object.keys(PLAN_LIMITS).map((k) => [k, z.number().int().min(1).max(1_000_000).nullable().optional()])) as Record<PlanLimit, z.ZodOptional<z.ZodNullable<z.ZodNumber>>>)
  .partial();

export const planInput = z.object({
  key: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_-]{1,39}$/, 'Lower case letters, digits, - and _'),
  name: z.string().trim().min(1, 'Required').max(80),
  description: z.string().trim().max(500).nullable().optional(),
  seatPriceId: optionalPriceId,
  /** Shown in the app only (pence per seat per month). Stripe holds the real price. */
  seatPriceMinor: z.number().int().min(0).max(10_000_000).nullable().optional(),
  currency: z.string().trim().length(3).default('GBP'),
  interval: z.enum(['month', 'year']).default('month'),
  modules: z.array(z.enum(PLAN_MODULE_KEYS as [PlanModule, ...PlanModule[]])).max(10),
  features: z.array(z.enum(Object.keys(PLAN_FEATURES) as [PlanFeature, ...PlanFeature[]])).max(10).default([]),
  limits: limitsInput.default({}),
  addons: z.array(planAddonInput).max(10).default([]),
  trialDays: z.number().int().min(0).max(90).default(14),
  /** Tenants can pick it (Checkout and change plan). */
  selectable: z.boolean().default(true),
  /** The plan new sign-ups trial on. Only one plan is the default. */
  isDefault: z.boolean().default(false),
  active: z.boolean().default(true),
  sort: z.number().int().min(0).max(1000).default(0),
});
export type PlanInput = z.infer<typeof planInput>;
export const planUpdateInput = planInput.omit({ key: true }).partial();

// --- Tenant actions --------------------------------------------------------------------------

export const checkoutInput = z.object({
  planKey: z.string().trim().min(1).max(40),
  addons: z.array(z.string().max(40)).max(10).default([]),
});

export const subscriptionChangeInput = z
  .object({
    planKey: z.string().trim().min(1).max(40).optional(),
    seats: z.number().int().min(1).max(10_000).optional(),
    addons: z.array(z.string().max(40)).max(10).optional(),
    /** Unix seconds, from the preview, so the charge matches what was shown. */
    prorationDate: z.number().int().positive().optional(),
  })
  .refine((v) => v.planKey !== undefined || v.seats !== undefined || v.addons !== undefined, 'Nothing to change');
export type SubscriptionChangeInput = z.infer<typeof subscriptionChangeInput>;

export const subscriptionCancelInput = z.object({
  /** false = resume a cancellation that is scheduled for the period end. */
  cancel: z.boolean(),
});

/** What the change preview returns: Stripe's prorated invoice, in minor units. */
export interface SubscriptionPreview {
  amountDue: number;
  subtotal: number;
  tax: number | null;
  currency: string;
  prorationDate: number;
  /** When the amount is charged: now (an increase billed immediately) or at the next renewal. */
  nextPaymentAt: string | null;
  lines: { description: string; amount: number; proration: boolean }[];
}
